import type {ReactNode} from 'react';
import {BrandingProvider} from '../branding';
import {CartProvider} from '../cart';
import {I18nProvider} from '../i18n';
import {MenuProvider} from '../menu';
import {OrderProvider} from '../order';
import {GuestSessionProvider, ServiceStatusProvider} from '../session';
import {GuestStreamProvider} from '../stream';
import {ThemeStyles} from '../theme';
import {UiStyles} from '../ui';
import type {ApiClient} from '../api';
import {ErrorBoundary} from './ErrorBoundary';
import {RouterProvider, useRouter} from './router';
import {ROUTES} from './routes';
import {ROUTE_PATTERNS, screenForPattern} from './screens';
import {useGuestSession} from '../session';
import Q19InvalidQr from '../screens/Q19InvalidQr';

/**
 * The application shell.
 *
 * Provider order is load-bearing and reads top to bottom as a dependency chain:
 *
 *   I18n            language, and nothing depends on it that it depends on
 *   GuestSession    owns the credential AND the API client bound to it
 *   ServiceStatus   one service-status read, needs the credential; publishes
 *                   the merchant's timezone into I18n for every screen below
 *   Branding        one branding read, needs the credential; applies the
 *                   merchant accent (a <style> override) and publishes the logo
 *   GuestStream     one SSE connection, needs the credential
 *   Menu            the projection plus live 86s off the stream
 *   Cart            needs the menu to know what has sold out
 *   Order           needs the cart to submit it
 *   Router          screens need everything above
 *
 * The shell renders **no chrome** — no header, no navigation, no footer. Every
 * pixel a guest sees belongs to a screen, because the eleven screens are eleven
 * different moments (a landing that must show venue identity in three seconds, a
 * dead end, a receipt) and a shared header would be wrong on most of them.
 */
export function App({client}: {client?: ApiClient}) {
  return (
    <>
      <ThemeStyles />
      {/*
        The shared primitive styles. `Screen` renders this too — React 19
        de-duplicates by `href`, so it appears once — and it is mounted here as
        well so the very first frame has it, before any screen has resolved.
      */}
      <UiStyles />
      <I18nProvider>
        <ErrorBoundary>
          <GuestSessionProvider client={client}>
            <ServiceStatusProvider>
              <BrandingProvider>
                <GuestStreamProvider>
                  <MenuProvider>
                    <CartProvider>
                      <OrderProvider>
                        <RouterProvider patterns={ROUTE_PATTERNS}>
                          <ScreenOutlet />
                        </RouterProvider>
                      </OrderProvider>
                    </CartProvider>
                  </MenuProvider>
                </GuestStreamProvider>
              </BrandingProvider>
            </ServiceStatusProvider>
          </GuestSessionProvider>
        </ErrorBoundary>
      </I18nProvider>
    </>
  );
}

/**
 * Renders whichever screen owns the current URL.
 *
 * An unmatched URL falls to Q19 rather than to a 404: a guest is here because
 * they scanned something, and "this QR does not work, ask staff" is the true and
 * useful answer to a URL this surface does not recognise. A generic 404 is
 * neither.
 */
function ScreenOutlet(): ReactNode {
  const {pattern} = useRouter();
  const {status} = useGuestSession();
  const Screen = screenForPattern(pattern) ?? Q19InvalidQr;

  // **A spent QR shows "scan again" wherever the guest refreshed, not only on the
  // landing.** When the sitting is over — the bill was paid and the table cleared,
  // or the till voided it — the server answers this session `unauthenticated`, the
  // provider clears it (and every crumb it left, via `clearGuestData`) and the
  // status lands on `expired`. A guest who reloads `/menu` or `/status` on that
  // dead QR must meet Q19, not a half-loaded screen quietly failing every call
  // (owner, 2026-08-18).
  //
  // The scan and queue landings are exempt: they OWN the re-scan — a fresh scan
  // arrives with a still-expired status in storage, and they call `open()` to
  // turn it into a live session. Overriding them with Q19 would wedge the very
  // action that recovers the guest.
  const isLanding = pattern === ROUTES.scan || pattern === ROUTES.queue;
  if ((status === 'expired' || status === 'invalid') && !isLanding) {
    return <Q19InvalidQr />;
  }
  return <Screen />;
}
